import { useMemo } from 'react';
import { isEmail, isValidEmail } from '@atlaskit/user-picker';
import { token } from '@atlaskit/tokens';
import { N0, N600 } from '@atlaskit/theme/colors';
import { getExperimentValue } from '../../feature-gate';

import { type OptionType } from '../../components/InviteeList/InviteeListContext';
import { ExperimentKeys } from '../../types';

const getIds = (userPickerOptions: OptionType[]): string[] => {
	return userPickerOptions.map((opt) => {
		return opt.id;
	});
};

const getIsMultiValue = (state: any, userPickerOptions: OptionType[]) => {
	const ids = new Set(getIds(userPickerOptions));
	return state?.options?.filter((val: any) => !ids.has(val?.data?.id))?.length > 1;
};

const getIsEmailVisible = (state: any, userPickerOptions: OptionType[]) => {
	const email = state?.options?.filter((option: any) => isEmail(option.data))?.at(0);

	if (!email) {
		return false;
	}

	const ids = new Set(getIds(userPickerOptions));
	return !ids.has(email.data?.id);
};

export const useStickyExperimentStyles = (userPickerOptions: OptionType[]) => {
	const isExperimentEnabled = getIsExperimentEnabled();

	return useMemo(() => {
		if (!isExperimentEnabled) {
			return {};
		}

		return {
			option: (css: any, state: any) => {
				const optionData = state?.data?.data;
				const isSticky = optionData && isEmail(optionData);
				const isDisabled = isValidEmail(optionData?.id) === 'INVALID';
				const isMultiValue = getIsMultiValue(state, userPickerOptions);

				const colorAndShadowOptions = {
					boxShadow: css['boxShadow'],
					backgroundColor: css['backgroundColor'],
				};

				const multiValueStyle = {
					backgroundColor: token('elevation.surface.overlay', N0),
					borderTop: `${token('space.025', '2px')} solid ${token('color.border', N600)}`,
					padding: `${token('space.100', '8px')} 0`,
					marginTop: token('space.100', '8px'),
					borderRadius: `0 0 ${token('space.050', '4px')} ${token('space.050', '4px')}`,
					boxShadow: 'None',
					'> span:first-child': {
						padding: `${token('space.075', '6px')} ${token('space.150', '12px')}`,
						opacity: isDisabled && token('opacity.disabled'),
						...colorAndShadowOptions,
					},
				};

				const disabledStyle = {
					'> *:first-child': {
						opacity: token('opacity.disabled'),
					},
				};

				const stickyOptions = {
					position: 'sticky',
					bottom: 0,
					...(isDisabled && disabledStyle),
					...(isMultiValue && multiValueStyle),
				};

				return {
					...css,
					overflow: 'hidden',
					...(isSticky && stickyOptions),
				};
			},
			menuList: (css: any, state: any) => {
				const isEmailVisible = getIsEmailVisible(state, userPickerOptions);

				const multiValueStyle = {
					paddingBottom: 0,
				};

				const isMultiValue = getIsMultiValue(state, userPickerOptions);

				return {
					...css,
					zIndex: '400',
					...(isMultiValue && isEmailVisible && multiValueStyle),
				};
			},
		};
	}, [userPickerOptions, isExperimentEnabled]);
};

const getIsExperimentEnabled = () =>
	getExperimentValue(ExperimentKeys.STICKY_EMAIL_INVITE, 'control') === 'experiment';

export const useIsStickyExperimentEnabled = (override: boolean) =>
	getIsExperimentEnabled() && override;
